import React, { useEffect, useRef, useState } from "react";
import {
  MdArrowDownward,
  MdArrowUpward,
  MdClose,
  MdOutlineRoute,
} from "react-icons/md";

import CircularLoader from "../../../../../../../Components/CircularLoader/CircularLoader";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import { fetchOrdersByIds } from "../../../../../../../services/orders.services";
import {
  createRun,
  fetchRunDrivers,
} from "../../../../../../../services/ShipperServices/DeliveryRoutes/deliveryRuns.services";
import NewDriverForm, { NEW_DRIVER } from "./NewDriverForm";
import RunDialog from "./RunDialog";
import {
  byPostalCode,
  CANCELLED_STATUS,
  deliveryAddress,
  FULFILLED_STATUS,
  orderPieces,
  orderWeight,
} from "./routeSheet";
import { bestStopOrder, SORT_PROBLEMS } from "./runOrder";
import {
  driverName,
  mainRouteId,
  nextWorkingDay,
  RUN_MAX_STOPS,
  runChipText,
} from "./runs";

const FIELD =
  "w-full rounded-[6px] border-[1px] border-ui-border-strong bg-ui-surface px-[9px] py-[6px] text-[12.5px] text-ui-text focus:border-brand focus:outline-none";
const LABEL =
  "text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted";
const ICON_BTN =
  "grid h-[26px] w-[26px] shrink-0 place-items-center rounded-[6px] text-ui-text-subtle hover:bg-ui-surface-sunken hover:text-ui-text disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent";

// The orders load without their pallets' history: nothing here reads it, and
// on a busy product it's most of an order's size.
const LOAD = { palletHistory: false };
const formatLb = (lb) =>
  `${lb >= 10 ? Math.round(lb).toLocaleString("en-CA") : Math.round(lb * 10) / 10} lb`;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const customerName = (shipping) =>
  [shipping?.first_name, shipping?.last_name]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ");

// Why an order can't go on a new run (the API refuses the same).
export const leftOutReason = (order) => {
  if (order?.open_run_stop?.run_id) {
    return `already on ${runChipText(order.open_run_stop) || "an open run"}`;
  }
  const status = Number(order?.order_status_id);
  if (status === FULFILLED_STATUS) return "already fulfilled";
  if (status === CANCELLED_STATUS || order?.status === "Cancelled")
    return "cancelled";
  return "";
};

/**
 * Create run: the ticked self-delivery orders become a run's stops, in the
 * order Google Maps finds shortest to drive from the start location and back
 * (by postal code when it can't say; ↑/↓ to change, × to leave one out),
 * with a date (next working day by default), the start location, a driver
 * and notes. Orders already on an open run, fulfilled or cancelled are left
 * out with a note. `onCreated(run)` gets the run the API returned.
 */
const CreateRunModal = ({
  open,
  orderIds = [],
  locations = [],
  defaultLocationId = null,
  onCreated,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(0);
  const [stops, setStops] = useState([]);
  const [leftOut, setLeftOut] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [runDate, setRunDate] = useState(nextWorkingDay());
  const [startId, setStartId] = useState("");
  const [driverId, setDriverId] = useState("");
  const [addingDriver, setAddingDriver] = useState(false);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // "", "sorting", "sorted", "moved" (by hand since), or a SORT_PROBLEMS key.
  const [sortState, setSortState] = useState("");
  const sortRequest = useRef(0);
  const tooMany = orderIds.length > RUN_MAX_STOPS;
  const addressOf = (id) =>
    locations.find((location) => String(location.id) === String(id))?.address ||
    "";

  // Puts the stops in the best order from the start address; a newer request
  // (another start, Best order again) wins over an older one.
  const sortStops = async (list, startAddress) => {
    const request = ++sortRequest.current;
    setSortState("sorting");
    const best = await bestStopOrder(
      startAddress,
      list.map((order) => ({
        id: order.id,
        address: deliveryAddress(order?.order_shipping_address_details),
        done: false,
      })),
    );
    if (request !== sortRequest.current) return;
    if (best.problem) {
      setSortState(best.problem);
      return;
    }
    const at = new Map(best.ids.map((id, index) => [id, index]));
    const place = (order) => at.get(order.id) ?? Number.MAX_SAFE_INTEGER;
    setStops((current) => [...current].sort((a, b) => place(a) - place(b)));
    setSortState("sorted");
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setFailed(0);
    setStops([]);
    setLeftOut([]);
    setRunDate(nextWorkingDay());
    setDriverId("");
    setAddingDriver(false);
    setNotes("");
    setError("");
    setSortState("");
    sortRequest.current += 1;
    if (tooMany) {
      setLoading(false);
      return undefined;
    }
    fetchRunDrivers().then((response) => {
      if (!cancelled && !response.error) {
        const list = response?.data?.result;
        setDrivers(Array.isArray(list) ? list : []);
      }
    });
    setLoading(true);
    // All the ticked orders in one request.
    fetchOrdersByIds(orderIds, LOAD).then(({ data: loaded }) => {
      if (cancelled) return;
      setFailed(orderIds.length - loaded.length);
      setLeftOut(
        loaded
          .map((order) => ({ order, reason: leftOutReason(order) }))
          .filter((entry) => entry.reason),
      );
      const usable = loaded
        .filter((order) => !leftOutReason(order))
        .sort(byPostalCode);
      setStops(usable);
      const shared = new Set(
        usable
          .filter((order) => order?.warehouse_id != null)
          .map((order) => String(order.warehouse_id)),
      );
      const own = shared.size === 1 ? [...shared][0] : null;
      const known = (id) =>
        locations.some((location) => String(location.id) === String(id));
      const chosen =
        own && known(own)
          ? own
          : known(defaultLocationId)
            ? String(defaultLocationId)
            : locations[0]
              ? String(locations[0].id)
              : "";
      setStartId(chosen);
      setLoading(false);
      sortStops(usable, addressOf(chosen));
    });
    return () => {
      cancelled = true;
    };
    // Loaded once per opening; the ticked orders don't change while it's open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const move = (index, delta) => {
    setStops((current) => {
      const next = [...current];
      const [stop] = next.splice(index, 1);
      next.splice(index + delta, 0, stop);
      return next;
    });
    setSortState((state) => (state === "sorted" ? "moved" : state));
  };
  const changeStart = (id) => {
    setStartId(id);
    sortStops(stops, addressOf(id));
  };
  const sorting = sortState === "sorting";
  const sortText =
    {
      sorting: "Finding the shortest drive…",
      sorted: "Shortest drive from the start and back; ↑/↓ to change.",
      moved: "↑/↓ to change; Best order sorts them again.",
    }[sortState] ??
    (SORT_PROBLEMS[sortState]
      ? `${SORT_PROBLEMS[sortState]}, so they're by postal code; ↑/↓ to change.`
      : "Sorted by postal code; ↑/↓ to change.");
  const remove = (index) =>
    setStops((current) => current.filter((_, at) => at !== index));

  const pieces = stops.reduce((sum, order) => sum + orderPieces(order), 0);
  const weights = stops.map(orderWeight);
  const weight = weights.every((value) => value != null)
    ? weights.reduce((sum, value) => sum + value, 0)
    : null;
  const summary = [
    plural(stops.length, "stop"),
    `${pieces} pcs`,
    weight != null && stops.length > 0 && formatLb(weight),
  ]
    .filter(Boolean)
    .join(" · ");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const response = await createRun({
        run_date: runDate,
        route_id: mainRouteId(stops),
        start_warehouse_id: startId ? Number(startId) : null,
        driver_id: driverId ? Number(driverId) : null,
        notes: notes.trim() || null,
        order_ids: stops.map((order) => order.id),
      });
      if (response.error) {
        setError(
          response.error?.response?.data?.message ||
            "Couldn't create the run. Try again.",
        );
        return;
      }
      onCreated(response?.data?.result);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RunDialog
      open={open}
      labelId="createRunTitle"
      title="Create run"
      subtitle={loading ? "Loading the orders…" : summary}
      widthClass="max-w-[820px]"
      closeDisabled={saving}
      onClose={onClose}
      footer={
        <>
          <BoltButton
            type="button"
            variant="outline"
            size="sm"
            label="Cancel"
            onClick={onClose}
            disabled={saving}
          />
          <BoltButton
            type="button"
            variant="primary"
            size="sm"
            label={saving ? "Creating…" : "Create run"}
            onClick={save}
            disabled={
              saving ||
              loading ||
              sorting ||
              tooMany ||
              addingDriver ||
              !stops.length ||
              !runDate
            }
          />
        </>
      }
    >
      {tooMany ? (
        <p className="m-[0px] rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg p-[12px] text-[12.5px] text-state-warning-text">
          A run takes up to {RUN_MAX_STOPS} orders. Untick some and create it
          again.
        </p>
      ) : loading ? (
        <div className="grid min-h-[160px] place-items-center">
          <CircularLoader size={24} />
        </div>
      ) : (
        <>
          <section className="grid gap-[10px] rounded-[7px] border-[1px] border-ui-border bg-ui-surface p-[12px] sm:grid-cols-3">
            <label className="grid gap-[4px]">
              <span className={LABEL}>Date</span>
              <input
                type="date"
                value={runDate}
                onChange={(event) => setRunDate(event.target.value)}
                className={FIELD}
              />
            </label>
            <label className="grid gap-[4px]">
              <span className={LABEL}>Start and end at</span>
              <select
                value={startId}
                onChange={(event) => changeStart(event.target.value)}
                className={FIELD}
              >
                {!locations.length && <option value="">No location</option>}
                {locations.map((location) => (
                  <option key={location.id} value={String(location.id)}>
                    {location.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-[4px]">
              <span className={LABEL}>Driver</span>
              <select
                value={driverId}
                onChange={(event) =>
                  event.target.value === NEW_DRIVER
                    ? setAddingDriver(true)
                    : setDriverId(event.target.value)
                }
                className={FIELD}
              >
                <option value="">No driver yet</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={String(driver.id)}>
                    {driverName(driver) || `User #${driver.id}`}
                    {driver.role === "Driver" ? " · Driver" : ""}
                  </option>
                ))}
                <option value={NEW_DRIVER}>+ New driver…</option>
              </select>
            </label>
            {addingDriver && (
              <NewDriverForm
                className="sm:col-span-3"
                onCancel={() => setAddingDriver(false)}
                onCreated={(driver) => {
                  setDrivers((list) => [
                    ...list.filter((entry) => entry.id !== driver.id),
                    driver,
                  ]);
                  setDriverId(String(driver.id));
                  setAddingDriver(false);
                }}
              />
            )}
            <label className="grid gap-[4px] sm:col-span-3">
              <span className={LABEL}>Notes (optional)</span>
              <textarea
                rows={2}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                className={FIELD}
              />
            </label>
          </section>

          {(failed > 0 || leftOut.length > 0) && (
            <div className="grid gap-[4px] rounded-[7px] border-[1px] border-state-warning-border bg-state-warning-bg px-[12px] py-[8px] text-[12px] text-state-warning-text">
              {failed > 0 && (
                <span>
                  {plural(failed, "order")} couldn't be loaded and{" "}
                  {failed === 1 ? "isn't" : "aren't"} on this run.
                </span>
              )}
              {leftOut.map(({ order, reason }) => (
                <span key={order.id}>
                  #{order.id} is left out: {reason}.
                </span>
              ))}
            </div>
          )}

          <section className="overflow-hidden rounded-[7px] border-[1px] border-ui-border bg-ui-surface">
            <div className="flex flex-wrap items-center justify-between gap-x-[10px] gap-y-[4px] border-b-[1px] border-ui-border px-[12px] py-[8px]">
              <span className={LABEL}>Stops in delivery order</span>
              <span className="flex flex-wrap items-center gap-x-[10px] gap-y-[4px]">
                <span role="status" className="text-[11px] text-ui-text-muted">
                  {sortText}
                </span>
                {stops.length > 1 && (
                  <button
                    type="button"
                    onClick={() => sortStops(stops, addressOf(startId))}
                    disabled={sorting}
                    title="Sort the stops for the shortest drive from the start and back"
                    className="flex shrink-0 items-center gap-[4px] border-0 bg-transparent p-0 text-[11.5px] font-semibold text-brand hover:underline disabled:cursor-wait disabled:opacity-50"
                  >
                    <MdOutlineRoute className="h-[13px] w-[13px] shrink-0" />
                    Best order
                  </button>
                )}
              </span>
            </div>
            {stops.length === 0 ? (
              <p className="m-[0px] px-[12px] py-[14px] text-[12px] text-ui-text-muted">
                No orders can go on a run.
              </p>
            ) : (
              <ol className="m-[0px] list-none p-[0px]">
                {stops.map((order, index) => (
                  <li
                    key={order.id}
                    className="flex items-center gap-[10px] border-b-[1px] border-ui-border-subtle px-[12px] py-[8px] last:border-b-0"
                  >
                    <span className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-brand text-[11px] font-bold text-white">
                      {index + 1}
                    </span>
                    <div className="grid min-w-[0px] flex-1 gap-[1px]">
                      <span className="truncate font-semibold">
                        #{order.id} ·{" "}
                        {customerName(order?.order_shipping_address_details) ||
                          "-"}
                      </span>
                      <span className="truncate text-ui-text-muted">
                        {deliveryAddress(
                          order?.order_shipping_address_details,
                        ) || "No delivery address"}
                      </span>
                    </div>
                    <span className="shrink-0 text-ui-text-muted">
                      {orderPieces(order)} pcs
                    </span>
                    <div className="flex shrink-0">
                      <button
                        type="button"
                        aria-label={`Move #${order.id} up`}
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        className={ICON_BTN}
                      >
                        <MdArrowUpward className="h-[14px] w-[14px]" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Move #${order.id} down`}
                        disabled={index === stops.length - 1}
                        onClick={() => move(index, 1)}
                        className={ICON_BTN}
                      >
                        <MdArrowDownward className="h-[14px] w-[14px]" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Leave #${order.id} out`}
                        onClick={() => remove(index)}
                        className={ICON_BTN}
                      >
                        <MdClose className="h-[14px] w-[14px]" />
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>

          {error && (
            <p
              role="alert"
              className="m-[0px] rounded-[7px] border-[1px] border-state-danger-border bg-state-danger-bg px-[12px] py-[8px] text-[12px] text-state-danger-text"
            >
              {error}
            </p>
          )}
        </>
      )}
    </RunDialog>
  );
};

export default CreateRunModal;
